import React, { useState } from 'react';
import AddonIcon from '@/shared/icons/AddonIcon';
import Button from '@/shared/components/UI/Button';
import Select from '@/shared/components/UI/Select';
import useDanteApi from '@/hooks/useDanteApi';
import * as plansService from '@/services/plans.service';
import { trackAddonSubscription } from '@/helpers/analytics';
import { useUserStore } from '@/stores/user/userStore';
import mixpanel from '@/utils/mixpanel';
import useModalStore from '@/stores/modal/modalStore';
import PlanInfoBox from './PlanInfoBox';
import FeaturesList from './FeaturesList';

const AddonSection = ({ addonConfig }) => {
  const user = useUserStore((state) => state.user);
  const { data: plans } = useDanteApi(plansService.getPlans);
  const [addonLoading, setAddonLoading] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(
    addonConfig?.quantityOptions?.[0]?.value ?? 1
  );

  const modalStore = useModalStore();
  const commonTrackingProps = {
    version: 'pricing-modal-v2',
    modal_variant: modalStore.modalVariant,
    modal_type: 'plan_and_addon',
    current_tier: modalStore.upgradeContext?.currentTier,
    target_tier: modalStore.upgradeContext?.targetTier,
    feature_name: modalStore.upgradeContext?.feature || null,
    source: modalStore.upgradeContext?.source || null,
    addon_key: addonConfig?.addonKey,
    addon_label: addonConfig?.label,
  };

  const selectedQtyOption = addonConfig?.quantityOptions?.find(
    (o) => o.value === selectedQuantity
  );

  const handleAddonClick = async () => {
    if (!addonConfig?.addonKey || !plans) return;
    const addonPlan = plans.find((p) => p.key === addonConfig.addonKey);
    if (!addonPlan) return;

    const quantity = addonConfig.hasInputQty ? selectedQuantity : undefined;

    setAddonLoading(true);
    try {
      const response = await plansService.getCheckoutSessionAddon(addonPlan.id, quantity);
      if (response?.status === 200) {
        const selectedOpt = addonConfig.quantityOptions?.find((o) => o.value === selectedQuantity);
        trackAddonSubscription({
          subscription_id: response.data.checkout_session.id,
          user_id: user?.id,
          email: user?.email,
          addon_name: addonPlan.name,
          addon_price: selectedOpt?.pricePerMonth ?? addonPlan.price,
        });
        mixpanel.track('Upgrade Modal Addon CTA Clicked', {
          ...commonTrackingProps,
          addon_price: selectedOpt?.pricePerMonth ?? addonPlan.price,
          quantity: addonConfig.hasInputQty ? selectedQuantity : undefined,
          cta_label: addonConfig.buttonLabel,
        });
        window.location.assign(response.data.checkout_session.url);
      }
    } catch (e) {
      mixpanel.track('Upgrade Modal Addon CTA Failed', {
        ...commonTrackingProps,
        addon_price: addonConfig?.price,
        quantity: addonConfig?.hasInputQty ? selectedQuantity : undefined,
        error_message: e?.message || 'Addon checkout error',
      });
      console.error('Addon checkout error:', e);
    } finally {
      setAddonLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-stretch flex-1 min-w-0 self-stretch">
      <div className="flex flex-col items-start w-full gap-v3-lg">
        <PlanInfoBox
          icon={React.createElement(addonConfig.icon ?? AddonIcon, {
            className: 'text-v3-purple-primary',
          })}
          title={addonConfig.label}
          subtitle={addonConfig.subtitle}
        />
        <FeaturesList features={addonConfig.features} />
        {addonConfig.hasInputQty && addonConfig.quantityOptions?.length > 0 ? (
          <div className="flex flex-col gap-v3-xs w-full">
            <p className="font-inter-variable text-sm font-medium leading-4 text-v3-black-max">
              Choose your credit package
            </p>
            <Select
              options={addonConfig.quantityOptions.map((o) => ({ value: o.value, label: o.label }))}
              value={selectedQuantity}
              onChange={(val) => {
                const prevQty = selectedQuantity;
                const newQty = Number(val);
                const newOpt = addonConfig?.quantityOptions?.find((o) => o.value === newQty);
                mixpanel.track('Upgrade Modal Addon Quantity Changed', {
                  ...commonTrackingProps,
                  previous_quantity: prevQty,
                  new_quantity: newQty,
                  price_per_month: newOpt?.pricePerMonth,
                  total_credits: newOpt?.credits,
                });
                setSelectedQuantity(newQty);
              }}
              placeholder="Select credits"
            />
            {selectedQtyOption && (
              <span className="font-inter-variable text-xs font-medium leading-4 text-v3-black-secondary">
                ${selectedQtyOption.pricePerMonth}/mo for{' '}
                {selectedQtyOption.credits.toLocaleString()} credits
              </span>
            )}
          </div>
        ) : (
          <span className="font-inter-variable text-xs font-medium leading-4 text-v3-black-secondary">
            {addonConfig.price}
          </span>
        )}
      </div>
      <div className="flex flex-col items-stretch w-full mt-auto pt-v3-lg">
        <Button
          variant="primary"
          size="md"
          label={addonConfig.buttonLabel}
          onClick={handleAddonClick}
          loading={addonLoading}
          loadingLabel="Loading…"
          className="w-full justify-center"
        />
      </div>
    </div>
  );
};

export default AddonSection;
